import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Search, 
  FileText, 
  Package, 
  Calendar, 
  Users, 
  ShoppingBag, 
  Building2,
  Loader2,
  MessageSquare
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useGlobalSearch, SearchResultType, SearchResult } from "@/hooks/useGlobalSearch";
import { Badge } from "@/components/ui/badge";

const TYPE_CONFIG: Record<SearchResultType, { label: string; icon: typeof FileText; pluralLabel: string }> = {
  lead: { label: "Lead", icon: FileText, pluralLabel: "Leads" },
  order: { label: "Order", icon: Package, pluralLabel: "Orders" },
  booking: { label: "Booking", icon: Calendar, pluralLabel: "Bookings" },
  supplier: { label: "Supplier", icon: Building2, pluralLabel: "Suppliers" },
  product: { label: "Product", icon: ShoppingBag, pluralLabel: "Products" },
  client: { label: "Client", icon: Users, pluralLabel: "Clients" },
  message: { label: "Message", icon: MessageSquare, pluralLabel: "Messages" },
};

export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { query, setQuery, groupedResults, isLoading } = useGlobalSearch();

  // Keyboard shortcut: Cmd+K or Ctrl+K
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const COFFEEBEANS_BRAND_ID = "c395c2fa-4c12-4692-8814-560fa8885ce5";
  const CREATIVE_TRAVEL_BRAND_ID = "ef0ce1ff-c11a-4641-9fa4-e6d9c2883670";

  const handleSelect = useCallback((type: SearchResultType, id: string, result?: SearchResult) => {
    setOpen(false);
    setQuery("");

    switch (type) {
      case "lead":
        navigate(`/lead/${id}`);
        break;
      case "order":
        navigate(`/operations/snapstack?order=${id}`);
        break;
      case "booking": {
        const brandId = result?.routeData?.brand_id;
        if (result?.routeData?.deleted) {
          navigate(`/operations/coffeebeans?view=deleted&booking=${id}`);
          break;
        }
        if (brandId === COFFEEBEANS_BRAND_ID) {
          navigate(`/operations/coffeebeans?booking=${id}`);
        } else if (brandId === CREATIVE_TRAVEL_BRAND_ID) {
          navigate(`/operations/creative-travel?booking=${id}`);
        } else {
          navigate(`/operations/coffeebeans?booking=${id}`);
        }
        break;
      }
      case "supplier":
        navigate(`/suppliers?view=${id}`);
        break;
      case "product":
        navigate(`/products?view=${id}`);
        break;
      case "client":
        navigate(`/department/sales?client=${encodeURIComponent(result?.title || id)}`);
        break;
      case "message": {
        if (result?.routeData?.channel_id) {
          navigate(`/?openMessaging=true&channelId=${result.routeData.channel_id}`);
        } else if (result?.routeData?.partner_id) {
          navigate(`/?openMessaging=true&dmId=${result.routeData.partner_id}&dmName=${encodeURIComponent(result.routeData.partner_name || '')}`);
        }
        break;
      }
    }
  }, [navigate, setQuery]);

  const hasResults = Object.values(groupedResults).some(group => group.length > 0);

  return (
    <>
      <Button
        variant="outline"
        size="icon"
        onClick={() => setOpen(true)}
        title="Search (⌘K)"
      >
        <Search className="h-4 w-4" />
      </Button>

      <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false}>
        <CommandInput
          placeholder="Search leads, orders, bookings, suppliers..."
          value={query}
          onValueChange={setQuery}
        />
        <CommandList>
          {isLoading && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {!isLoading && query.length >= 2 && !hasResults && (
            <CommandEmpty>No results found.</CommandEmpty>
          )}

          {!isLoading && query.length < 2 && (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Type at least 2 characters to search...
            </div>
          )}

          {!isLoading && hasResults && (
            <>
              {(Object.entries(groupedResults) as [SearchResultType, typeof groupedResults.lead][]).map(
                ([type, items]) => {
                  if (items.length === 0) return null;
                  const config = TYPE_CONFIG[type];
                  const Icon = config.icon;

                  return (
                    <CommandGroup key={type} heading={config.pluralLabel}>
                      {items.map((item) => (
                        <CommandItem
                          key={`${type}-${item.id}`}
                          value={`${item.title} ${item.subtitle || ""}`}
                          onSelect={() => handleSelect(type, item.id, item)}
                          className="cursor-pointer"
                        >
                          <Icon className="mr-2 h-4 w-4 text-muted-foreground" />
                          <div className="flex flex-col flex-1 min-w-0">
                            <span className="font-medium truncate">{item.title}</span>
                            {item.subtitle && (
                              <span className="text-xs text-muted-foreground truncate">
                                {item.subtitle}
                              </span>
                            )}
                          </div>
                          {item.meta && (
                            <Badge variant={item.meta.startsWith("Deleted") ? "destructive" : "secondary"} className="ml-2 text-xs capitalize">
                              {item.meta.replace(/_/g, " ")}
                            </Badge>
                          )}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  );
                }
              )}
            </>
          )}
        </CommandList>
      </CommandDialog>
    </>
  );
}
